import React from "react";
import { Col } from "react-bootstrap";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { PieChart as PieIcon, Zap, ShieldAlert } from "lucide-react";
import { motion } from "framer-motion";

const SectorAnalysis = ({ sectorComp, marketShare }) => {
    if (!sectorComp || !marketShare) return null;

    const compData = [
        { name: "Public Sector", value: sectorComp.market_share_2026.public_sector * 100 },
        { name: "Private Sector", value: sectorComp.market_share_2026.private_sector * 100 },
    ];

    const shareData = marketShare.map(item => ({
        name: item.construction_type,
        value: item.share_2026 * 100,
        focus: item.focus
    }));

    const COLORS = ["#CC0000", "#0B1220", "#4B5563", "#9CA3AF"];

    return (
        <div className="mt-12">
            <div className="d-flex align-items-center gap-2 mb-6">
                <div className="p-2 bg-red-50 rounded-lg text-red-600">
                    <PieIcon size={20} />
                </div>
                <h3 className="text-lg font-bold text-gray-800 m-0">Sector Composition & Risk Analysis</h3>
            </div>

            <div className="row g-4">
                <Col lg={4}>
                    <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 h-100 text-center">
                        <h4 className="text-xs text-gray-400 font-bold uppercase mb-6">Public vs. Private (2026)</h4>
                        <div style={{ height: 250 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={compData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={80}
                                        paddingAngle={5}
                                        dataKey="value"
                                    >
                                        {compData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                    <Legend verticalAlign="bottom" wrapperStyle={{ fontSize: '10px' }} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="mt-4 pt-4 border-t border-gray-50 text-left">
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <Zap size={14} className="text-yellow-500" />
                                <span className="text-xs font-bold text-gray-700">Growth Velocity</span>
                            </div>
                            <p className="text-xs text-gray-500 m-0">
                                <strong>Public:</strong> {sectorComp.growth_velocity.public_sector}<br />
                                <strong>Private:</strong> {sectorComp.growth_velocity.private_sector}
                            </p>
                        </div>
                    </div>
                </Col>

                <Col lg={8}>
                    <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 h-100">
                        <h4 className="text-xs text-gray-400 font-bold uppercase mb-4">Construction Type Focus (2026)</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {shareData.map((item, i) => (
                                <div key={i} className="p-4 bg-gray-50 rounded-2xl">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <span className="text-xs font-bold text-gray-600">{item.name}</span>
                                        <span className="text-sm font-black text-red-600">{item.value}%</span>
                                    </div>
                                    <p className="text-[10px] text-gray-400 leading-relaxed">
                                        <strong>Primary Focus:</strong> {item.focus}
                                    </p>
                                </div>
                            ))}
                        </div>

                        <div className="mt-6 p-4 bg-red-50 rounded-2xl d-flex gap-3 align-items-start border border-red-100">
                            <ShieldAlert size={20} className="text-red-600 mt-1 shrink-0" />
                            <div>
                                <h5 className="text-xs font-bold text-red-900 mb-1 uppercase tracking-wider">Strategic Risk Factors</h5>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-[10px] text-red-800 font-bold m-0">Public Sector Risk</p>
                                        <p className="text-[10px] text-red-600 m-0">{sectorComp.primary_risk.public_sector}</p>
                                    </div>
                                    <div>
                                        <p className="text-[10px] text-red-800 font-bold m-0">Private Sector Risk</p>
                                        <p className="text-[10px] text-red-600 m-0">{sectorComp.primary_risk.private_sector}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Col>
            </div>
        </div>
    );
};

export default SectorAnalysis;
